﻿(() => {
  const roots = document.querySelectorAll('[data-nf-sample-finder]');

  roots.forEach((root) => {
    const carousel = root.querySelector('[data-sample-carousel]');
    const stage = carousel?.querySelector('[data-carousel-stage]');
    const cards = carousel ? Array.from(carousel.querySelectorAll('[data-carousel-card]')) : [];
    const currentLabel = carousel?.querySelector('[data-carousel-current]');
    const previousButton = carousel?.querySelector('[data-carousel-previous]');
    const nextButton = carousel?.querySelector('[data-carousel-next]');
    const selectedCardIndex = cards.findIndex((card) => card.classList.contains('is-selected'));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const maxVisible = 7;
    const halfVisible = 3;
    const fanPositions = [
      { x: -116, y: 34, rotation: -20, scale: 0.78, zIndex: 1 },
      { x: -80, y: 18, rotation: -13, scale: 0.85, zIndex: 2 },
      { x: -42, y: 6, rotation: -7, scale: 0.94, zIndex: 3 },
      { x: 0, y: 0, rotation: 0, scale: 1, zIndex: 10 },
      { x: 42, y: 6, rotation: 7, scale: 0.94, zIndex: 3 },
      { x: 80, y: 18, rotation: 13, scale: 0.85, zIndex: 2 },
      { x: 116, y: 34, rotation: 20, scale: 0.78, zIndex: 1 },
    ];
    let currentIndex = selectedCardIndex >= 0 ? selectedCardIndex : 0;
    let dragStartX = null;
    let dragDistance = 0;
    let suppressClick = false;
    let hoveredSlot = null;
    let visibleMap = new Map();

    const responsiveMultiplier = () => {
      if (window.innerWidth < 480) return 0.56;
      if (window.innerWidth < 750) return 0.68;
      if (window.innerWidth < 1100) return 0.84;
      return 1;
    };

    const getVisibleMap = () => {
      const nextMap = new Map();
      const visibleCount = Math.min(maxVisible, cards.length);
      const centreSlot = Math.floor(visibleCount / 2);
      for (let slot = 0; slot < visibleCount; slot += 1) {
        const cardIndex = (currentIndex + slot - centreSlot + cards.length) % cards.length;
        nextMap.set(cardIndex, slot + (halfVisible - centreSlot));
      }
      return nextMap;
    };

    const getTransform = (slot, dragOffset = 0) => {
      const base = fanPositions[slot];
      const multiplier = responsiveMultiplier();
      let x = base.x * multiplier + dragOffset * 0.42;
      let y = base.y;
      let rotation = base.rotation + dragOffset * 0.018;
      let scale = base.scale;
      let zIndex = base.zIndex;

      if (hoveredSlot !== null && dragStartX === null) {
        const distance = Math.abs(slot - hoveredSlot);
        if (slot === hoveredSlot) {
          y -= 22;
          scale *= 1.055;
          zIndex = 20;
        } else {
          const push = Math.max(5, 17 - distance * 3) * multiplier;
          x += slot < hoveredSlot ? -push : push;
          rotation += slot < hoveredSlot ? -1.8 : 1.8;
        }
      }

      return {
        opacity: Math.max(0.44, 1 - Math.abs(slot - halfVisible) * 0.17),
        transform: 'translate(calc(-50% + ' + x + 'px), calc(-50% + ' + y + 'px)) rotate(' + rotation + 'deg) scale(' + scale + ')',
        zIndex,
      };
    };

    const renderCarousel = (dragOffset = 0, animateEntrance = false) => {
      visibleMap = getVisibleMap();

      cards.forEach((card, cardIndex) => {
        const slot = visibleMap.get(cardIndex);
        const isVisible = slot !== undefined;
        const isCurrent = cardIndex === currentIndex;
        let layout;

        if (isVisible) {
          layout = getTransform(slot, dragOffset);
        } else {
          const exitsLeft = ((cardIndex - currentIndex + cards.length) % cards.length) > cards.length / 2;
          const exitX = (exitsLeft ? -1 : 1) * Math.max(360, stage?.clientWidth || 360);
          layout = {
            opacity: 0,
            transform: 'translate(calc(-50% + ' + exitX + 'px), calc(-50% + 120px)) rotate(' + (exitsLeft ? -28 : 28) + 'deg) scale(.62)',
            zIndex: 0,
          };
        }

        card.style.zIndex = String(layout.zIndex);
        card.style.opacity = String(layout.opacity);
        card.style.transform = layout.transform;
        card.classList.toggle('is-visible', isVisible);
        card.classList.toggle('is-current', isCurrent);
        card.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
        const link = card.querySelector('a');
        if (link) link.tabIndex = isCurrent ? 0 : -1;

        if (animateEntrance && isVisible && !reducedMotion && card.animate) {
          const entranceDelay = 100 + Math.abs(slot - halfVisible) * 60;
          card.animate(
            [
              { opacity: 0, transform: layout.transform + ' translateY(150px) scale(.68)' },
              { opacity: layout.opacity, transform: layout.transform },
            ],
            { duration: 900, delay: entranceDelay, easing: 'cubic-bezier(.16, 1, .3, 1)' }
          );
        }
      });

      if (currentLabel) currentLabel.textContent = String(currentIndex + 1);
    };

    const move = (direction) => {
      if (!cards.length) return;
      currentIndex = (currentIndex + direction + cards.length) % cards.length;
      hoveredSlot = null;
      renderCarousel();
    };

    previousButton?.addEventListener('click', () => move(-1));
    nextButton?.addEventListener('click', () => move(1));

    stage?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      }
    });

    stage?.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      dragStartX = event.clientX;
      dragDistance = 0;
      suppressClick = false;
      hoveredSlot = null;
      stage.classList.add('is-dragging');
      stage.setPointerCapture?.(event.pointerId);
    });

    stage?.addEventListener('pointermove', (event) => {
      if (dragStartX === null) return;
      dragDistance = event.clientX - dragStartX;
      if (Math.abs(dragDistance) > 8) suppressClick = true;
      renderCarousel(dragDistance);
    });

    const finishDrag = () => {
      if (dragStartX === null) return;
      stage?.classList.remove('is-dragging');
      if (Math.abs(dragDistance) > 54) move(dragDistance < 0 ? 1 : -1);
      else renderCarousel();
      dragStartX = null;
      dragDistance = 0;
      window.setTimeout(() => { suppressClick = false; }, 0);
    };

    stage?.addEventListener('pointerup', finishDrag);
    stage?.addEventListener('pointercancel', finishDrag);

    cards.forEach((card, cardIndex) => {
      card.addEventListener('pointerenter', () => {
        const slot = visibleMap.get(cardIndex);
        if (slot === undefined || dragStartX !== null) return;
        hoveredSlot = slot;
        renderCarousel();
      });

      card.addEventListener('click', (event) => {
        if (suppressClick) {
          event.preventDefault();
          return;
        }
        if (cardIndex !== currentIndex && visibleMap.has(cardIndex)) {
          event.preventDefault();
          currentIndex = cardIndex;
          hoveredSlot = null;
          renderCarousel();
        }
      });
    });

    stage?.addEventListener('pointerleave', () => {
      if (dragStartX !== null) return;
      hoveredSlot = null;
      renderCarousel();
    });

    window.addEventListener('resize', () => renderCarousel());
    renderCarousel(0, true);

    const sortSelect = root.querySelector('[data-sample-sort]');
    const sortInput = root.querySelector('[data-sample-filter-sort]');
    sortSelect?.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    });
    if (sortSelect && sortInput) sortInput.value = sortSelect.value;

    const markSampleButtons = () => {
      root.querySelectorAll('[data-sample-product-card]').forEach((card) => {
        const sampleButton = card.querySelector('.js-product-samples button, [product-samples-button]');
        card.classList.toggle('has-sample-app', Boolean(sampleButton));
      });
    };

    markSampleButtons();
    const observer = new MutationObserver(markSampleButtons);
    observer.observe(root, { childList: true, subtree: true });
  });
})();
